import {
  FaroMenuSeparator,
  FaroText,
  grey,
  NO_TRANSLATE_CLASS,
} from "@faro-lotv/flat-ui";
import { List, ListItem } from "@mui/material";
import type { JSX } from "react";

/**
 * List of most popular CAD formats supported by Sphere XG.
 * The list is basically the one provided by Autodesk APS (https://aps.autodesk.com/en/docs/model-derivative/v2/developers_guide/supported-translations/)
 * for formats supporting SVF output,
 * and when input file is a standalone monolithic file (as Sphere XG does not support yet multi-file CAD models).
 */
const POPULAR_SUPPORTED_CAD_FORMATS = [
  "NWD, NWC (Navisworks)",
  "RVT (Revit)",
  "IFC",
  "DWG (AutoCAD)",
];

/**
 * List of other CAD formats supported by Sphere XG.
 * The list is basically the one provided by Autodesk APS (https://aps.autodesk.com/en/docs/model-derivative/v2/developers_guide/supported-translations/)
 * for formats supporting SVF output,
 * and when input file is a standalone monolithic file (as Sphere XG does not support yet multi-file CAD models).
 */
const SUPPORTED_CAD_FORMATS = [
  "3DM (Rhino)",
  "3DS (3ds Max)",
  "A",
  "ASM (Creo)",
  "ASM (Solid Edge)",
  "ASM, ASM0, ASM1...",
  "BRD",
  "CATPART (CATIA)",
  "CGR",
  "COLLABORATION",
  "DAE",
  "DGN",
  "DLV3",
  "DWF",
  "DWFX",
  "DWG",
  "DWT",
  "DXF",
  "EMODEL",
  "EXP",
  "F3D",
  "FBX",
  "G",
  "GBXML",
  "GLB",
  "IAM (Inventor)",
  "IDW (Inventor)",
  "IFC",
  "IGE",
  "IGES",
  "IGS",
  "IPT (Inventor)",
  "IWM",
  "JT",
  "MAX",
  "MODEL",
  "MPF",
  "MSR",
  "NEU, NEU0, NEU1...",
  "NWC",
  "NWD",
  "OBJ",
  "PAR",
  "PDF",
  "PMLPRJ",
  "PMLPRJZ",
  "PRT (Creo)",
  "PRT (NX)",
  "PRT0, PRT1...",
  "PSM",
  "PSMODEL",
  "RVM",
  "RVT",
  "SAB (ACIS)",
  "SAT (ACIS)",
  "SCH",
  "SESSION",
  "SKP",
  "SLDPRT (SOLIDWORKS)",
  "STE",
  "STEP",
  "STL",
  "STLA",
  "STLB",
  "STP (STEP)",
  "STPZ (Compressed STEP)",
  "VUE",
  "WIRE (Alias)",
  "X_B (Parasolid)",
  "X_T (Parasolid)",
  "XAS",
  "XPR",
];

/**
 * List of supported CAD extensions in Sphere XG.
 * The Data Management dropzone needs to know which file extensions to associate with the 3D model
 * import on drag & drop.
 */
export const SUPPORTED_CAD_EXTENSIONS_FOR_DRAG_AND_DROP = [
  ".3dm",
  ".3ds",
  ".a",
  ".asm",
  ".brd",
  ".catpart",
  ".cgr",
  ".collaboration",
  ".dae",
  ".dgn",
  ".dlv3",
  ".dwf",
  ".dwfx",
  ".dwg",
  ".dwt",
  ".dxf",
  ".emodel",
  ".exp",
  ".f3d",
  ".fbx",
  ".g",
  ".gbxml",
  ".glb",
  ".iam",
  ".idw",
  ".ifc",
  ".ige",
  ".iges",
  ".igs",
  ".ipt",
  ".iwm",
  ".jt",
  ".max",
  ".model",
  ".mpf",
  ".msr",
  ".neu",
  ".nwc",
  ".nwd",
  ".obj",
  ".par",
  ".pdf",
  ".pmlprj",
  ".pmlprjz",
  ".prt",
  ".psm",
  ".psmodel",
  ".rvm",
  ".rvt",
  ".sab",
  ".sat",
  ".sch",
  ".session",
  ".skp",
  ".sldprt",
  ".ste",
  ".step",
  ".stl",
  ".stla",
  ".stlb",
  ".stp",
  ".stpz",
  ".vue",
  ".wire",
  ".x_b",
  ".x_t",
  ".xas",
  ".xpr",
];

type SubListCadFormatsProps = {
  /** list of CAD formats */
  listFormats: string[];
};

/**
 * Display a subset of the list of supported CAD formats.
 *
 * @returns The a list of supported CAD formats as a MUI's List
 */
function SubListCadFormats({
  listFormats,
}: SubListCadFormatsProps): JSX.Element {
  return (
    <List disablePadding dense sx={{ marginBottom: 1, marginTop: 1 }}>
      {listFormats.map((format) => (
        <ListItem key={format}>
          <FaroText
            variant="bodyM"
            color={grey[800]}
            className={NO_TRANSLATE_CLASS}
          >
            {format}
          </FaroText>
        </ListItem>
      ))}
    </List>
  );
}
/**
 * Display the list of supported CAD formats as a plain text.
 *
 * @returns The list of supported CAD formats as a MUI's List
 */
export function ListSupportedCadFormats(): JSX.Element {
  return (
    <>
      <SubListCadFormats listFormats={POPULAR_SUPPORTED_CAD_FORMATS} />
      <FaroMenuSeparator />
      <SubListCadFormats listFormats={SUPPORTED_CAD_FORMATS} />
    </>
  );
}
